import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { MailCheck, MailWarning, RefreshCw, LogOut, ArrowRight } from "lucide-react";

// Handles: /confirm-email (waiting for the sign-up confirmation), /verify-email?token=, /confirm-email-change?token=
const afterConfirm = (fallback = "/me") => {
  const n = localStorage.getItem("velluto_after_confirm");
  if (n) localStorage.removeItem("velluto_after_confirm");
  return n || fallback;
};

export default function EmailConfirm() {
  const { pathname } = useLocation();
  const [params] = useSearchParams();
  const token = params.get("token");
  const { user, refresh, setUser } = useAuth();
  const nav = useNavigate();
  const [state, setState] = useState(token ? "working" : "waiting");
  const [error, setError] = useState("");
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const done = useRef(false);
  const nextRef = useRef(null);

  const isChange = pathname === "/confirm-email-change";

  useEffect(() => {
    if (!token || done.current) return;
    done.current = true;
    const endpoint = isChange ? "/auth/confirm-email-change" : "/auth/verify-email";
    api.post(endpoint, { token })
      .then(async () => { setState("done"); if (user) await refresh(); })
      .catch((e) => { setState("error"); setError(formatApiErrorDetail(e.response?.data?.detail)); });
  }, [token, isChange, user, refresh]);

  useEffect(() => {
    if (state !== "waiting" || !user) return;
    if (user.email_verified) { nav(afterConfirm(), { replace: true }); return; }
    const poll = setInterval(async () => { const u = await refresh(); if (u?.email_verified) { toast.success("Email confirmed — welcome in"); nav(afterConfirm(), { replace: true }); } }, 5000);
    return () => clearInterval(poll);
  }, [state, user, refresh, nav]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const resend = async () => {
    setResending(true);
    try { await api.post("/auth/resend-verification"); toast.success("A fresh confirmation email is on its way"); setCooldown(60); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setResending(false); }
  };
  const logout = async () => { await api.post("/auth/logout").catch(() => {}); setUser(false); nav("/auth"); };

  const Shell = ({ icon: Icon, tone = "gold", title, children }) => (
    <div className="min-h-screen flex items-center justify-center px-6 pt-16">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
        data-testid="email-confirm-page" className="w-full max-w-md rounded-3xl border border-white/10 bg-surface p-8 sm:p-10 text-center">
        <span className={`w-16 h-16 mx-auto rounded-full border flex items-center justify-center ${tone === "gold" ? "border-gold/40 bg-gold/10 text-gold" : "border-red-500/40 bg-red-500/10 text-red-400"}`}><Icon size={26} /></span>
        <h1 className="font-display text-4xl font-light mt-6">{title}</h1>
        {children}
      </motion.div>
    </div>
  );

  if (state === "working") return <Shell icon={RefreshCw} title="Confirming…"><p className="text-text-muted text-sm mt-3">One moment.</p></Shell>;

  if (state === "done") {
    return (
      <Shell icon={MailCheck} title={isChange ? "Email updated" : "You're confirmed"}>
        <p data-testid="email-confirm-success" className="text-text-muted text-sm mt-3">
          {isChange ? "Your account now uses the new address. Sign in with it from now on." : "Your email is confirmed and your account is active. Welcome to Velluto."}
        </p>
        <Link to={user ? (isChange ? "/me" : (nextRef.current ??= afterConfirm("/verify"))) : "/auth"} data-testid="email-confirm-continue"
          className="inline-flex items-center gap-2 mt-8 px-8 py-4 rounded-full bg-gold text-ink text-xs uppercase tracking-[0.2em] hover:bg-gold/90 transition-colors">
          {user ? "Continue" : "Sign in"} <ArrowRight size={14} />
        </Link>
      </Shell>
    );
  }

  if (state === "error") {
    return (
      <Shell icon={MailWarning} tone="red" title="That link didn't work">
        <p data-testid="email-confirm-error" className="text-red-400 text-sm mt-3">{error}</p>
        {user && !user.email_verified && !isChange && (
          <button data-testid="email-resend-btn" onClick={resend} disabled={resending || cooldown > 0}
            className="mt-8 px-8 py-4 rounded-full bg-gold text-ink text-xs uppercase tracking-[0.2em] hover:bg-gold/90 disabled:opacity-50">
            {cooldown > 0 ? `Resend in ${cooldown}s` : "Send a new link"}
          </button>
        )}
        <div className="mt-6"><Link to={user ? "/me" : "/auth"} className="text-xs text-text-muted hover:text-text-main">{user ? "Back to profile" : "Back to sign in"}</Link></div>
      </Shell>
    );
  }

  // waiting (signed in, email not yet confirmed)
  return (
    <Shell icon={MailCheck} title="Confirm your email">
      <p className="text-text-muted text-sm mt-3">
        We sent a confirmation link to <span data-testid="pending-email" className="text-text-main">{user?.email}</span>. Tap it to activate your account — this page will update on its own.
      </p>
      <p className="text-[11px] text-text-muted mt-3">Check your spam folder if it hasn't arrived within a minute.</p>
      <div className="mt-8 flex flex-col gap-3">
        <button data-testid="email-resend-btn" onClick={resend} disabled={resending || cooldown > 0}
          className="flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-gold text-ink text-xs uppercase tracking-[0.2em] hover:bg-gold/90 transition-colors disabled:opacity-50">
          <RefreshCw size={14} className={resending ? "animate-spin" : ""} /> {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend email"}
        </button>
        <button data-testid="email-confirm-logout" onClick={logout} className="flex items-center justify-center gap-2 px-8 py-3 rounded-full border border-white/15 text-text-muted text-xs uppercase tracking-[0.15em] hover:text-text-main transition-colors">
          <LogOut size={14} /> Use a different account
        </button>
      </div>
    </Shell>
  );
}
